const pages = {
  home: {
    hero: {
      subtitle: "Strategic Energy Procurement Advisory",
      title: "Expert Solutions for Crude Oil & LNG",
      titleHighlight: "Procurement",
      description: "Medigreen LLC provides specialized advisory services for Crude Oil and LNG procurement. We combine deep market expertise with a global network to deliver optimal outcomes for energy buyers.",
      ctaPrimary: "Explore Our Services",
      ctaSecondary: "Free Consultation",
    },
    services: {
      sectionTitle: "Our Services",
      title: "Total Solutions",
      titleHighlight: "for Energy Procurement",
      description: "From market intelligence to contract execution, Medigreen provides the full spectrum of advisory services to optimize your energy procurement strategy.",
      cta: "View All Services",
      items: {
        crudeOil: {
          title: "Crude Oil Procurement",
          desc: "End-to-end advisory for crude oil sourcing — from supplier identification and market analysis to contract negotiation and logistics coordination.",
        },
        lng: {
          title: "LNG Procurement Strategy",
          desc: "Comprehensive LNG procurement support including long-term contract structuring, spot market optimization, and price risk management.",
        },
        market: {
          title: "Market Analysis & Forecasting",
          desc: "Proprietary data-driven analysis of global energy markets, price trends, and supply-demand dynamics to inform procurement decisions.",
        },
        contract: {
          title: "Contract Negotiation Support",
          desc: "Expert guidance through complex energy contract negotiations, ensuring favorable terms, risk mitigation, and regulatory compliance.",
        },
      },
    },
    about: {
      sectionTitle: "About Medigreen LLC",
      title: "Your Trusted Partner",
      titleHighlight: "in Energy Procurement",
      description1: "Medigreen LLC is a specialized consulting firm focused on Crude Oil and LNG procurement advisory. We combine deep market knowledge with rigorous analytical capabilities to help energy buyers achieve optimal procurement outcomes.",
      description2: "Our team of seasoned professionals brings decades of experience in international energy markets, supply chain management, and contract law — providing clients with a decisive competitive advantage in procurement negotiations.",
      cta: "Learn More About Us",
      badge: {
        label: "Global Network",
        value: "40+",
        sublabel: "Countries & Regions",
      },
      strengths: {
        global: {
          title: "Global Network",
          desc: "Access to suppliers and counterparties across the Middle East, Southeast Asia, Australia, and the Americas.",
        },
        risk: {
          title: "Risk Management",
          desc: "Comprehensive price risk and supply security strategies tailored to your business needs.",
        },
        team: {
          title: "Expert Team",
          desc: "Consultants with 20+ years of hands-on experience in international energy trading and procurement.",
        },
      },
    },
    stats: {
      years: "Years of Industry Experience",
      deals: "Procurement Deals Advised",
      clients: "Global Energy Clients",
      value: "USD in Procurement Value",
    },
    cta: {
      badge: "Contact Us",
      title: "Let's Solve",
      titleHighlight: "Your Energy Challenges",
      heading: "Ready to Optimize Your Energy Procurement?",
      description: "Whether you're navigating volatile crude oil markets or structuring long-term LNG contracts, Medigreen's experts are ready to help. Our first consultation is complimentary.",
      benefits: [
        "Free initial consultation",
        "Tailored procurement strategy",
        "Access to global supplier network",
        "Ongoing market intelligence support",
      ],
      button: "Schedule a Free Consultation",
    },
    news: {
      sectionTitle: "Latest News",
      title: "Market Insights & Updates",
      cta: "View All News",
    },
  },
  services: {
    hero: {
      subtitle: "What We Do",
      title: "Our Services",
      description: "Comprehensive energy procurement advisory services designed to help you achieve optimal outcomes in Crude Oil and LNG markets.",
    },
    items: {
      crudeOil: {
        title: "Crude Oil Procurement Advisory",
        desc: "We provide comprehensive advisory services for crude oil procurement, covering supplier identification, market analysis, price benchmarking, and contract negotiation. Our team leverages deep relationships with producers across the Middle East, West Africa, and the Americas to secure optimal supply terms.",
        features: [
          "Supplier identification & qualification",
          "Price benchmarking & market analysis",
          "Contract structuring & negotiation",
          "Logistics & shipping coordination",
          "Quality inspection support",
        ],
      },
      lng: {
        title: "LNG Procurement Strategy",
        desc: "Our LNG advisory services help buyers navigate the complex global LNG market. From long-term supply agreements to spot cargo procurement, we optimize your LNG portfolio to balance cost, security, and flexibility.",
        features: [
          "Long-term contract structuring",
          "Spot & short-term cargo sourcing",
          "Price formula optimization",
          "Destination flexibility negotiation",
          "Regasification terminal advisory",
        ],
      },
      market: {
        title: "Market Analysis & Price Forecasting",
        desc: "Medigreen's proprietary analytical framework delivers actionable market intelligence. We monitor global supply-demand dynamics, geopolitical developments, and macroeconomic factors to provide accurate price forecasts and procurement timing recommendations.",
        features: [
          "Weekly & monthly market reports",
          "Price scenario modeling",
          "Supply disruption risk assessment",
          "Demand outlook analysis",
          "Competitor procurement benchmarking",
        ],
      },
      contract: {
        title: "Contract Negotiation Support",
        desc: "Our experienced negotiators work alongside your team to secure favorable contract terms. We bring deep knowledge of industry-standard contract structures, pricing mechanisms, and risk allocation frameworks.",
        features: [
          "Term sheet review & drafting",
          "Price review clause negotiation",
          "Force majeure & liability terms",
          "Dispute resolution mechanisms",
          "Regulatory compliance review",
        ],
      },
      risk: {
        title: "Supply Chain Risk Management",
        desc: "We help clients identify, assess, and mitigate supply chain risks across their energy procurement portfolio. Our risk management frameworks ensure business continuity even in volatile market conditions.",
        features: [
          "Supply security assessment",
          "Counterparty credit risk analysis",
          "Geopolitical risk monitoring",
          "Contingency supply planning",
          "Insurance & hedging advisory",
        ],
      },
      regulatory: {
        title: "Regulatory & Compliance Advisory",
        desc: "Navigating the complex regulatory landscape of international energy trade requires specialized expertise. Medigreen provides guidance on import/export regulations, sanctions compliance, and environmental standards.",
        features: [
          "Import/export regulation guidance",
          "Sanctions compliance screening",
          "Environmental standards advisory",
          "Carbon footprint assessment",
          "Government liaison support",
        ],
      },
    },
    cta: {
      title: "Ready to Get Started?",
      description: "Contact us today for a free initial consultation and discover how Medigreen can transform your energy procurement strategy.",
      button: "Contact Us",
    },
  },
  about: {
    hero: {
      subtitle: "Who We Are",
      title: "About Medigreen LLC",
      description: "A specialized energy procurement advisory firm built on expertise, integrity, and a relentless focus on client outcomes.",
    },
    story: {
      subtitle: "Our Story",
      title: "Founded on Deep Industry Expertise",
      paragraphs: [
        "Medigreen LLC was established to address a critical gap in the energy market: the need for truly independent, expert advisory services for energy buyers navigating the complex world of Crude Oil and LNG procurement.",
        "What sets us apart is the breadth and depth of our industry network. Built over years of engagement across producers, traders, financiers, and regulators worldwide, our connections give clients direct access to opportunities and insights that are simply unavailable through conventional channels.",
        "By leveraging this extensive network, Medigreen delivers services that go beyond advice — we open doors, facilitate introductions, and drive outcomes. From Japan and South Korea to Southeast Asia and the Middle East, our network is your competitive advantage in today's volatile energy markets.",
      ],
      cta: "Work With Us",
    },
    values: {
      subtitle: "Our Values",
      title: "What Drives Us",
      items: {
        integrity: { title: "Integrity", desc: "We operate with complete transparency and always act in our clients' best interests." },
        expertise: { title: "Expertise", desc: "Deep market knowledge and decades of hands-on experience in global energy markets." },
        global: { title: "Global Reach", desc: "Established relationships with suppliers, traders, and counterparties worldwide." },
        results: { title: "Results-Driven", desc: "We measure success by the tangible value we deliver to our clients' bottom line." },
      },
    },
    company: {
      subtitle: "Company Information",
      title: "Our Office",
      address: "Address",
      phone: "Phone",
      email: "Email",
    },
    team: {
      subtitle: "Our People",
      title: "Meet the Team",
      members: {
        yasunori: {
          name: "Yasunori Kawauchi",
          title: "Founder & CEO",
          bio: "A seasoned leader with an extensive network across global energy producers, traders, and financial institutions. Yasunori drives Medigreen's strategic vision and client relationships worldwide.",
        },
        kevin: {
          name: "Kevin Yamazaki",
          title: "Founder & CTO",
          bio: "Kevin brings deep expertise in digital infrastructure and data-driven market intelligence, enabling Medigreen to deliver cutting-edge analytical tools and technology solutions for energy procurement.",
        },
      },
    },
  },
  contact: {
    hero: {
      subtitle: "Get In Touch",
      title: "Contact Us",
      description: "Ready to optimize your energy procurement? Reach out for a free initial consultation.",
    },
    info: {
      subtitle: "Our Office",
      title: "Let's Start a Conversation",
      description: "We welcome inquiries from energy buyers, utilities, and industrial companies seeking expert procurement advisory support.",
      items: {
        address: "Address",
        phone: "Phone",
        email: "Email",
        hours: "Business Hours",
      },
      hoursValue: "Mon–Fri: 9:00 AM – 6:00 PM ChST",
    },
    form: {
      title: "Send Us a Message",
      name: "Full Name *",
      namePlaceholder: "John Smith",
      email: "Email Address *",
      emailPlaceholder: "john@company.com",
      company: "Company",
      companyPlaceholder: "Your Company Name",
      subject: "Subject",
      subjectOptions: [
        { value: "", label: "Select a topic..." },
        { value: "Crude Oil Procurement", label: "Crude Oil Procurement" },
        { value: "LNG Procurement", label: "LNG Procurement" },
        { value: "Market Analysis", label: "Market Analysis" },
        { value: "Contract Negotiation", label: "Contract Negotiation" },
        { value: "General Inquiry", label: "General Inquiry" },
      ],
      message: "Message *",
      messagePlaceholder: "Tell us about your energy procurement needs...",
      charCount: "{count}/500",
      charError: "Message cannot exceed 500 characters.",
      submit: "Send Message",
      sending: "Sending...",
      error: "Something went wrong. Please try again.",
      success: {
        title: "Message Sent!",
        description: "Thank you for reaching out. We'll get back to you within 1 business day.",
      },
    },
  },
  news: {
    hero: {
      subtitle: "Stay Informed",
      title: "News & Insights",
      description: "Market intelligence, industry analysis, and company updates from the Medigreen team.",
    },
    articles: {
      readMore: "Read More",
    },
  },
  caseStudies: {
    hero: {
      subtitle: "Proven Results",
      title: "Case Studies",
      description: "Real results for real clients. Explore how Medigreen has delivered measurable value across energy procurement engagements.",
    },
    cta: {
      title: "Become Our Next Success Story",
      description: "Let Medigreen help you achieve measurable results in your energy procurement strategy.",
      button: "Start a Conversation",
    },
  },
  notFound: {
    title: "Page Not Found",
    description: "The page you are looking for doesn't exist or has been moved.",
    button: "Back to Home",
  },
  privacy: {
    hero: {
      subtitle: "Legal",
      title: "Privacy Policy",
      updated: "Last Updated: April 3, 2026",
    },
    sections: [
      {
        title: "1. Introduction",
        content: "Medigreen LLC (\"we\", \"our\", or \"us\") is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or engage with our advisory services.",
      },
      {
        title: "2. Information We Collect",
        content: "We may collect personal information that you voluntarily provide to us when you fill out our contact form, request a consultation, or communicate with us by email. This may include your name, email address, company name, phone number, and details about your energy procurement needs.",
      },
      {
        title: "3. How We Use Your Information",
        content: "We use the information we collect to respond to your inquiries and provide advisory services, send you relevant market insights and updates (with your consent), improve our website and services, comply with applicable laws and regulations, and protect our legal rights and interests.",
      },
      {
        title: "4. Information Sharing",
        content: "We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as necessary to provide our services, comply with legal obligations, or protect our rights. We may share information with trusted service providers who assist us in operating our website and conducting our business.",
      },
      {
        title: "5. Data Security",
        content: "We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.",
      },
      {
        title: "6. Cookies",
        content: "Our website may use cookies and similar tracking technologies to enhance your browsing experience. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, some features of our website may not function properly without cookies.",
      },
      {
        title: "7. Third-Party Links",
        content: "Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those sites. We encourage you to review the privacy policies of any third-party sites you visit.",
      },
      {
        title: "8. Your Rights",
        content: "Depending on your location, you may have certain rights regarding your personal information, including the right to access, correct, or delete your data. To exercise these rights, please contact us at info@medigreen.energy.",
      },
      {
        title: "9. Changes to This Policy",
        content: "We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page with an updated date. Your continued use of our website after any changes constitutes your acceptance of the new policy.",
      },
      {
        title: "10. Contact Us",
        content: "If you have any questions about this Privacy Policy, please contact us at: Medigreen LLC, 1411 Pale San Vitores Road, Suite 303, Tamuning, GU | Email: info@medigreen.energy | Phone: +1.671.482.0104",
      },
    ],
  },
  terms: {
    hero: {
      subtitle: "Legal",
      title: "Terms of Use",
      updated: "Last Updated: April 3, 2026",
    },
    sections: [
      {
        title: "1. Acceptance of Terms",
        content: "By accessing and using the Medigreen LLC website (\"Site\"), you accept and agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use our Site.",
      },
      {
        title: "2. Use of the Site",
        content: "You may use this Site for lawful purposes only. You agree not to use the Site in any way that violates applicable laws or regulations, transmits harmful or offensive content, attempts to gain unauthorized access to any part of the Site, or interferes with the proper functioning of the Site.",
      },
      {
        title: "3. Intellectual Property",
        content: "All content on this Site, including text, graphics, logos, and images, is the property of Medigreen LLC and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.",
      },
      {
        title: "4. Advisory Services Disclaimer",
        content: "The information provided on this Site is for general informational purposes only and does not constitute professional advisory, legal, or financial advice. Engagement of Medigreen LLC\'s advisory services is subject to a separate written agreement. Past performance does not guarantee future results.",
      },
      {
        title: "5. Limitation of Liability",
        content: "To the fullest extent permitted by law, Medigreen LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of this Site or reliance on any information provided herein.",
      },
      {
        title: "6. Confidentiality",
        content: "Any information you submit through this Site, including inquiries and consultation requests, will be treated with strict confidentiality. We will not disclose your information to third parties without your consent, except as required by law.",
      },
      {
        title: "7. Third-Party Links",
        content: "This Site may contain links to third-party websites for your convenience. Medigreen LLC does not endorse or assume responsibility for the content, privacy policies, or practices of any third-party sites.",
      },
      {
        title: "8. Modifications",
        content: "Medigreen LLC reserves the right to modify these Terms of Use at any time. Changes will be effective immediately upon posting to the Site. Your continued use of the Site after any changes constitutes your acceptance of the revised terms.",
      },
      {
        title: "9. Governing Law",
        content: "These Terms of Use shall be governed by and construed in accordance with the laws of Guam, USA, without regard to its conflict of law provisions.",
      },
      {
        title: "10. Contact Us",
        content: "If you have any questions about these Terms of Use, please contact us at: Medigreen LLC, 1411 Pale San Vitores Road, Suite 303, Tamuning, GU | Email: info@medigreen.energy | Phone: +1.671.482.0104",
      },
    ],
  },
};

export default pages;